import { useCallback, useSyncExternalStore } from 'react';

/** Per-browser display preferences. Nothing here affects the game state. */
export interface Settings {
  /** Decorative texture on property colors. Off by default: it is noisy. */
  cardPatterns: boolean;
  /** Chimes for game events that need this player's attention. */
  alertSounds: boolean;
  /** Alert sound volume from 0 to 100. */
  alertVolume: number;
}

const DEFAULTS: Settings = { cardPatterns: false, alertSounds: false, alertVolume: 50 };
const KEY = 'lafayette:settings';

let current: Settings = read();
const listeners = new Set<() => void>();

function read(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

/** Mirrors settings onto the document so plain CSS can react to them. */
export function applySettings(settings: Settings = current): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.cardPatterns = settings.cardPatterns ? 'on' : 'off';
}

export function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): void {
  current = { ...current, [key]: value };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    // A browser with storage disabled just loses the preference on reload.
  }
  applySettings();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSettings(): [
  Settings,
  <K extends keyof Settings>(key: K, value: Settings[K]) => void,
] {
  const settings = useSyncExternalStore(
    subscribe,
    () => current,
    () => DEFAULTS,
  );
  const set = useCallback(
    <K extends keyof Settings>(key: K, value: Settings[K]) => setSetting(key, value),
    [],
  );
  return [settings, set];
}
